import nodemailer, { type Transporter } from 'nodemailer'
import { readFileSync, existsSync } from 'fs'
import { join } from 'path'
import type { Logger } from 'pino'
import type { LoggerReporter } from '@gzl10/nexus-sdk'
import type { MailConfig, SendMailOptions, SendMailResult } from './mail.types.js'

// Re-export types for backwards compatibility
export type { MailAction, SendMailOptions, SendMailResult } from './mail.types.js'

// Assets en public/ para que se publiquen con el paquete npm
const TEMPLATE_REL_PATH = join('public', 'mail', 'base.html')
// Logo blanco para header azul (fondo #3B82F6)
const LOGO_REL_PATH = join('public', 'nexus', 'nexus-light-512.png')

/**
 * Singleton mail service
 */
let mailServiceInstance: MailService | null = null

export function getMailService(): MailService {
  if (!mailServiceInstance) {
    throw new Error('MailService not initialized. Call initMailService() first.')
  }
  return mailServiceInstance
}

export function initMailService(
  config: MailConfig,
  logger: Logger,
  loggerService?: LoggerReporter,
  options?: { libPath?: string }
): MailService {
  mailServiceInstance = new MailService(config, logger, loggerService, options)
  return mailServiceInstance
}

export class MailService {
  private transporter: Transporter
  private defaultFrom: string
  private defaultLogoUrl: string
  private logger: Logger
  private template: string
  private loggerService?: LoggerReporter

  constructor(
    config: MailConfig,
    logger: Logger,
    loggerService?: LoggerReporter,
    options?: { libPath?: string }
  ) {
    this.defaultFrom = config.from
    this.logger = logger.child({ service: 'mail' })
    this.loggerService = loggerService

    const libPath = options?.libPath ?? process.cwd()
    this.template = readFileSync(join(libPath, TEMPLATE_REL_PATH), 'utf-8')

    // Cargar logo como base64 data URI
    const logoPath = join(libPath, LOGO_REL_PATH)
    if (existsSync(logoPath)) {
      const logoBase64 = readFileSync(logoPath).toString('base64')
      this.defaultLogoUrl = `data:image/png;base64,${logoBase64}`
    } else {
      this.defaultLogoUrl = ''
    }

    this.transporter = nodemailer.createTransport({
      host: config.host,
      port: config.port,
      secure: config.secure,
      auth: config.auth,
      // MailHog y servidores dev no necesitan TLS
      ...(config.auth ? {} : { ignoreTLS: true })
    })
  }

  async send(options: SendMailOptions): Promise<SendMailResult | null> {
    const from = options.from ?? this.defaultFrom
    const to = Array.isArray(options.to) ? options.to.join(', ') : options.to

    // Si hay title o message, usar template; si no, usar html raw
    const html = (options.title || options.message)
      ? this.renderTemplate(options)
      : options.html

    this.logger.info({ to, subject: options.subject }, 'Sending email')

    try {
      const result = await this.transporter.sendMail({
        from,
        to,
        subject: options.subject,
        text: options.text,
        html,
        replyTo: options.replyTo,
        attachments: options.attachments
      })

      this.logger.info(
        { messageId: result.messageId, accepted: result.accepted },
        'Email sent'
      )

      return {
        messageId: result.messageId,
        accepted: result.accepted as string[],
        rejected: result.rejected as string[]
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to send email')
      this.logger.warn({ error, to, subject: options.subject }, 'Failed to send email (continuing)')
      this.loggerService?.captureException(err, { service: 'mail', action: 'send', toCount: options.to?.length ?? 0 })
      return null
    }
  }

  private renderTemplate(options: SendMailOptions): string {
    let html = this.template

    // Reemplazar variables simples
    html = html.replace(/\{\{subject\}\}/g, options.subject)
    html = html.replace(/\{\{logoUrl\}\}/g, options.logoUrl ?? this.defaultLogoUrl)
    html = html.replace(/\{\{year\}\}/g, new Date().getFullYear().toString())

    // Procesar bloques condicionales con patrón específico
    html = this.processConditionalBlock(html, 'title', options.title)
    html = this.processConditionalBlock(html, 'message', options.message)

    // Actions (botones) - Nexus Blue (#3B82F6)
    if (options.actions?.length) {
      const actionsHtml = options.actions.map(a =>
        `<a href="${a.url}" style="display:inline-block; background-color:#3B82F6; color:#ffffff; padding:12px 24px; text-decoration:none; border-radius:6px; margin:4px; font-weight:500;">${a.label}</a>`
      ).join('')
      html = html.replace(/\{\{#if actions\}\}([\s\S]*?)\{\{\/if\}\}/g, (_, content) =>
        content.replace(/\{\{actions\}\}/g, actionsHtml)
      )
    } else {
      html = html.replace(/\{\{#if actions\}\}[\s\S]*?\{\{\/if\}\}/g, '')
    }

    return html
  }

  private processConditionalBlock(html: string, name: string, value?: string): string {
    const pattern = new RegExp(`\\{\\{#if ${name}\\}\\}([\\s\\S]*?)\\{\\{\\/if\\}\\}`, 'g')
    if (value) {
      return html.replace(pattern, (_, content) =>
        content.replace(new RegExp(`\\{\\{${name}\\}\\}`, 'g'), value)
      )
    }
    return html.replace(pattern, '')
  }

  async verify(): Promise<boolean> {
    try {
      await this.transporter.verify()
      this.logger.info('SMTP connection verified')
      return true
    } catch (error) {
      const err = error instanceof Error ? error : new Error('SMTP connection failed')
      this.logger.warn({ error }, 'SMTP connection failed (emails may not work)')
      this.loggerService?.captureException(err, { service: 'mail', action: 'verify' })
      return false
    }
  }
}
